/** Search analytics (/search-analytics): admin-only search metrics, top queries and content gaps */

import { useState, type ReactNode } from 'react'
import { AlertCircle, MousePointerClick, Search, ShieldAlert, TrendingUp } from 'lucide-react'
import { useSearchAnalytics } from '../hooks/use-search-analytics'
import { useAppStore } from '../stores/app-store'
import { useT, type TranslationKey } from '../i18n'
import { cn } from '../lib/utils'
import { cardClass, errorTextClass } from '../components/ui/styles'

type Period = '7d' | '30d'
const PERIODS: Period[] = ['7d', '30d']
const PERIOD_KEYS: Record<Period, TranslationKey> = { '7d': 'searchAnalytics.period.7d', '30d': 'searchAnalytics.period.30d' }
const TYPE_KEYS: Record<string, TranslationKey> = {
  hybrid: 'searchAnalytics.types.hybrid',
  keyword: 'searchAnalytics.types.keyword',
  semantic: 'searchAnalytics.types.semantic',
}
const TYPE_DOTS: Record<string, string> = { hybrid: 'bg-accent', keyword: 'bg-success', semantic: 'bg-warning' }
const TOP_QUERY_LIMIT = 15

const headingClass = 'text-sm font-semibold text-fg'

export function SearchAnalyticsPage() {
  const t = useT()
  const locale = useAppStore((s) => s.locale)
  const [period, setPeriod] = useState<Period>('7d')
  const { data, isLoading, error, refetch } = useSearchAnalytics(period)
  const numberFormat = new Intl.NumberFormat(locale)
  // The API reports click-through as a 0-100 percentage
  const percentFormat = new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 1 })
  const isForbidden = error?.status === 403

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6 px-4 py-6 md:px-8 md:py-8">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="font-display text-2xl font-semibold tracking-tight text-fg">{t('searchAnalytics.title')}</h1>
          <p className="mt-1 text-sm text-fg-muted">{t('searchAnalytics.subtitle')}</p>
        </div>
        {!isForbidden && (
          <div
            role="radiogroup"
            aria-label={t('searchAnalytics.period.label')}
            className="inline-flex shrink-0 self-start rounded-md border border-border bg-surface-muted p-0.5"
          >
            {PERIODS.map((p) => (
              <button
                key={p}
                type="button"
                role="radio"
                aria-checked={period === p}
                onClick={() => setPeriod(p)}
                className={cn(
                  'min-h-8 cursor-pointer rounded px-3 text-sm font-medium transition-colors',
                  period === p ? 'bg-surface-raised text-fg shadow-sm' : 'text-fg-muted hover:text-fg',
                )}
              >
                {t(PERIOD_KEYS[p])}
              </button>
            ))}
          </div>
        )}
      </header>

      {isForbidden ? (
        <div className={cn(cardClass, 'flex items-start gap-3 p-5')} role="alert">
          <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-warning" aria-hidden="true" />
          <div>
            <p className="text-sm font-medium text-fg">{t('searchAnalytics.adminRequired')}</p>
            <p className="mt-0.5 text-sm text-fg-muted">{t('searchAnalytics.adminRequiredHint')}</p>
          </div>
        </div>
      ) : error && !data ? (
        <div className={errorTextClass} role="alert">
          {t('searchAnalytics.error')}{' '}
          <button type="button" onClick={() => void refetch()} className="cursor-pointer font-medium underline underline-offset-2">
            {t('common.retry')}
          </button>
        </div>
      ) : isLoading || !data ? (
        <p className="py-12 text-center text-sm text-fg-subtle">{t('common.loading')}</p>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <MetricCard icon={<Search className="h-4 w-4" />} label={t('searchAnalytics.metric.total')} value={numberFormat.format(data.totalSearches)} />
            <MetricCard
              icon={<MousePointerClick className="h-4 w-4" />}
              label={t('searchAnalytics.metric.ctr')}
              value={percentFormat.format(data.clickThroughRate / 100)}
            />
            <MetricCard
              icon={<AlertCircle className="h-4 w-4" />}
              label={t('searchAnalytics.metric.zero')}
              value={numberFormat.format(data.zeroResultQueries.length)}
            />
          </div>

          <section aria-labelledby="sa-top" className={cn(cardClass, 'p-5')}>
            <div className="mb-3 flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-accent-text" aria-hidden="true" />
              <h2 id="sa-top" className={headingClass}>
                {t('searchAnalytics.top.heading')}
              </h2>
            </div>
            {data.topQueries.length === 0 ? (
              <p className="text-sm text-fg-subtle">{t('searchAnalytics.top.empty')}</p>
            ) : (
              <div className="-mx-1 overflow-x-auto">
                <table className="w-full min-w-[20rem] text-sm">
                  <thead>
                    <tr className="text-xs text-fg-subtle">
                      <th className="px-1 pb-2 text-left font-medium">{t('searchAnalytics.top.query')}</th>
                      <th className="px-1 pb-2 text-right font-medium">{t('searchAnalytics.top.count')}</th>
                      <th className="px-1 pb-2 text-right font-medium">{t('searchAnalytics.top.avgResults')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.topQueries.slice(0, TOP_QUERY_LIMIT).map((q) => (
                      <tr key={q.query} className="border-t border-border text-fg-muted">
                        <td className="break-all px-1 py-1.5 text-fg">{q.query}</td>
                        <td className="px-1 py-1.5 text-right tabular-nums">{numberFormat.format(q.count)}</td>
                        <td className="px-1 py-1.5 text-right tabular-nums">{numberFormat.format(q.avgResults)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {data.zeroResultQueries.length > 0 && (
            <section aria-labelledby="sa-gaps" className={cn(cardClass, 'p-5')}>
              <div className="mb-1 flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-warning" aria-hidden="true" />
                <h2 id="sa-gaps" className={headingClass}>
                  {t('searchAnalytics.gaps.heading')}
                </h2>
              </div>
              <p className="mb-3 text-sm text-fg-muted">{t('searchAnalytics.gaps.hint')}</p>
              <ul className="flex flex-wrap gap-2">
                {data.zeroResultQueries.map((q) => (
                  <li
                    key={q.query}
                    title={t('searchAnalytics.gaps.count', { count: numberFormat.format(q.count) })}
                    className="inline-flex max-w-full items-center gap-1.5 rounded-md bg-warning-soft px-2 py-1 text-xs text-fg"
                  >
                    <span className="break-all">{q.query}</span>
                    <span className="text-fg-subtle tabular-nums">×{numberFormat.format(q.count)}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {data.searchTypeDistribution.length > 0 && (
            <section aria-labelledby="sa-types" className={cn(cardClass, 'p-5')}>
              <h2 id="sa-types" className={cn('mb-3', headingClass)}>
                {t('searchAnalytics.types.heading')}
              </h2>
              <ul className="flex flex-wrap gap-x-6 gap-y-2">
                {data.searchTypeDistribution.map((d) => {
                  const key = TYPE_KEYS[d.type]
                  return (
                    <li key={d.type} className="flex items-center gap-2 text-sm text-fg-muted">
                      <span className={cn('h-2.5 w-2.5 rounded-sm', TYPE_DOTS[d.type] ?? 'bg-fg-subtle')} aria-hidden="true" />
                      {key ? t(key) : d.type}: <span className="font-medium text-fg tabular-nums">{numberFormat.format(d.count)}</span>
                    </li>
                  )
                })}
              </ul>
            </section>
          )}
        </>
      )}
    </div>
  )
}

function MetricCard({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div className={cn(cardClass, 'p-5')}>
      <p className="flex items-center gap-2 text-sm text-fg-muted">
        <span className="text-fg-subtle" aria-hidden="true">
          {icon}
        </span>
        {label}
      </p>
      <p className="mt-1 font-display text-2xl font-semibold tracking-tight text-fg tabular-nums">{value}</p>
    </div>
  )
}
